import { useCallback, useEffect, useState } from "react";
import { Loader2, X, Pencil, Trash2, Plus, AlertTriangle, MapPin } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtTime, fmtErr } from "../api";

// Employee daily attendance timeline — sessions paired from the backend's immutable
// attendance events. Headline "Total Inside" ALWAYS comes from the canonical daily
// record (AttendanceRecord.total_hours); working time is never derived in the browser
// as final-out minus first-in.
const pad = (n) => String(n).padStart(2, "0");
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fmtDur = (mins) => {
  if (mins == null || isNaN(mins)) return "—";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};
const EVENT_LABEL = { enter: "TIME-IN", exit: "TIME-OUT" };

const Timeline = ({ user, branches, canCorrect, onClose }) => {
  const [date, setDate] = useState(todayStr());
  const [events, setEvents] = useState([]);
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [correcting, setCorrecting] = useState(null); // event | "new"
  const [deleting, setDeleting] = useState(null);
  const [form, setForm] = useState({ timestamp: "", event_type: "enter", branch_id: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [ev, rec] = await Promise.allSettled([
        staffApi.get("/attendance/events", { params: { user_id: user.id, date, limit: 200 } }),
        staffApi.get(`/attendance/user/${user.id}`, { params: { limit: 365 } }),
      ]);
      if (ev.status === "fulfilled") {
        const list = Array.isArray(ev.value.data) ? ev.value.data : [];
        list.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        setEvents(list);
      } else {
        setError(fmtErr(ev.reason));
      }
      if (rec.status === "fulfilled" && Array.isArray(rec.value.data)) {
        setRecord(rec.value.data.find((r) => r.date === date) || null);
      }
    } finally {
      setLoading(false);
    }
  }, [user.id, date]);

  useEffect(() => { load(); }, [load]);

  // ---- derived display values ----
  const enters = events.filter((e) => e.event_type === "enter");
  const exits = events.filter((e) => e.event_type === "exit");
  const firstIn = enters[0]?.timestamp || record?.check_in_time;
  const finalOut = exits.length ? exits[exits.length - 1].timestamp : record?.check_out_time;
  const reentries = Math.max(0, enters.length - 1);
  // session-gap outside time (legitimate pair math — NOT final-minus-first)
  let outsideMin = null;
  if (events.length > 1) {
    let gap = 0;
    for (let i = 0; i < events.length - 1; i++) {
      if (events[i].event_type === "exit" && events[i + 1].event_type === "enter") {
        gap += (new Date(events[i + 1].timestamp) - new Date(events[i].timestamp)) / 60000;
      }
    }
    outsideMin = gap;
  }
  // canonical inside total: backend daily record only
  const insideMin = record?.total_hours != null ? Math.round(record.total_hours * 60) : null;
  const lastVerified = events.length ? events[events.length - 1].timestamp : null;
  const branchRadius = (bid) => (branches || []).find((b) => b.id === bid)?.radius_m;
  const isGpsException = (e) => {
    const r = branchRadius(e.branch_id);
    return r != null && e.distance_m != null && e.distance_m > r;
  };
  const anyException = events.some(isGpsException);

  // ---- corrections (SuperAdmin only) ----
  const openCorrect = (ev) => {
    setForm({ timestamp: toLocalInput(ev.timestamp), event_type: ev.event_type, branch_id: ev.branch_id || "", reason: "" });
    setFormError("");
    setCorrecting(ev);
  };
  const openNew = () => {
    setForm({ timestamp: `${date}T09:00`, event_type: "enter", branch_id: user.primary_branch_id || "", reason: "" });
    setFormError("");
    setCorrecting("new");
  };

  const submitCorrection = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.reason || form.reason.trim().length < 3) { setFormError("A correction reason is required (min 3 characters)."); return; }
    if (correcting === "new" && !form.branch_id) { setFormError("Select the property/site for this missed event."); return; }
    const label = correcting === "new"
      ? `Add a missed ${form.event_type === "enter" ? "TIME-IN" : "TIME-OUT"} at ${form.timestamp.replace("T", " ")} for ${user.name}?\n\nReason: ${form.reason}`
      : `Correct this event to ${form.event_type === "enter" ? "TIME-IN" : "TIME-OUT"} at ${form.timestamp.replace("T", " ")}?\n\nReason: ${form.reason}`;
    if (!window.confirm(label)) return;
    setBusy(true);
    try {
      if (correcting === "new") {
        const br = (branches || []).find((b) => b.id === form.branch_id);
        await staffApi.post("/attendance/event", {
          event_type: form.event_type,
          latitude: br?.latitude ?? 0,
          longitude: br?.longitude ?? 0,
          branch_id: form.branch_id || null,
          source: "admin",
          device_info: { via: "GR CONNECT", reason: form.reason.trim(), at: form.timestamp },
        });
        toast.success("Missed attendance event added.");
      } else {
        await staffApi.patch(`/attendance/events/${correcting.id}`, {
          timestamp: new Date(form.timestamp).toISOString(),
          event_type: form.event_type,
          reason: form.reason.trim(),
        });
        toast.success("Attendance event corrected.");
      }
      setCorrecting(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const submitDelete = async () => {
    if (!form.reason || form.reason.trim().length < 3) { setFormError("A reason is required (min 3 characters)."); return; }
    if (!window.confirm(`Delete this invalid ${deleting.event_type === "enter" ? "TIME-IN" : "TIME-OUT"} event?\n\nReason: ${form.reason}\n\nThe deletion is recorded in the audit log.`)) return;
    setBusy(true);
    try {
      await staffApi.delete(`/attendance/events/${deleting.id}`, { params: { reason: form.reason.trim() } });
      toast.success("Invalid event deleted (audited).");
      setDeleting(null);
      setForm({ ...form, reason: "" });
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="timeline-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-slate-50 h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#0A1128] text-white px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-display text-base font-bold" data-testid="timeline-user-name">{user.name}</h2>
            <p className="text-[11px] text-slate-400">{user.employee_code || "—"}{user.department ? ` · ${user.department}` : ""}{user.designation ? ` · ${user.designation}` : ""}</p>
          </div>
          <button onClick={onClose} data-testid="timeline-close" className="p-2 hover:bg-white/10 rounded-sm"><X size={16} /></button>
        </div>

        <div className="p-6">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="timeline-date" className={inputCls} />
            </div>
            {canCorrect && (
              <button onClick={openNew} data-testid="timeline-add-event"
                className="inline-flex items-center gap-1.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-4 py-2.5 text-[11px] uppercase tracking-widest rounded-sm transition-colors">
                <Plus size={12} /> Add Missed Event
              </button>
            )}
          </div>

          {loading ? (
            <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading timeline…</p>
          ) : error ? (
            <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="timeline-error">{error}</p>
          ) : (
            <>
              {/* summary — canonical totals */}
              <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="timeline-summary">
                {[
                  ["First Time-In", fmtTime(firstIn), "tl-first-in"],
                  ["Final Time-Out", fmtTime(finalOut), "tl-final-out"],
                  ["Total Inside", insideMin != null ? fmtDur(insideMin) : "—", "tl-inside"],
                  ["Total Outside", outsideMin != null ? fmtDur(outsideMin) : "—", "tl-outside"],
                  ["Entries", enters.length, "tl-entries"],
                  ["Exits", exits.length, "tl-exits"],
                  ["Re-entries", reentries, "tl-reentries"],
                  ["Status", record?.status || (events.length ? "Recorded" : "—"), "tl-status"],
                ].map(([label, value, tid]) => (
                  <div key={tid} className="bg-white border border-slate-200 p-3" data-testid={tid}>
                    <p className="font-mono-tech text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">{label}</p>
                    <p className="mt-1 font-display text-sm font-bold text-slate-900">{String(value)}</p>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]" data-testid="timeline-flags">
                {(record?.branch_name || enters[0]?.branch_name) && (
                  <span className="inline-flex items-center gap-1 border border-slate-200 bg-white px-2.5 py-1 text-slate-600">
                    <MapPin size={11} className="text-[#C5221F]" /> {record?.branch_name || enters[0]?.branch_name}
                  </span>
                )}
                {anyException && (
                  <span className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-2.5 py-1 text-amber-700 font-semibold" data-testid="tl-gps-exception">
                    <AlertTriangle size={11} /> GPS / Location Exception
                  </span>
                )}
                {lastVerified && <span className="text-slate-400" data-testid="tl-last-verified">Last verified {fmtTime(lastVerified)}</span>}
              </div>

              {/* event timeline */}
              {events.length === 0 ? (
                <p className="mt-8 text-sm text-slate-500 bg-white border border-slate-200 p-6" data-testid="timeline-empty">No attendance events recorded for this date.</p>
              ) : (
                <ol className="mt-6 space-y-0" data-testid="timeline-events">
                  {events.map((ev, i) => {
                    const isEnter = ev.event_type === "enter";
                    const isReentry = isEnter && enters.indexOf(ev) > 0;
                    const corrected = ev.corrected || ev.corrected_at || ev.correction_reason;
                    return (
                      <li key={ev.id || i} className="relative pl-8 pb-5 border-l-2 border-slate-200 last:border-transparent" data-testid={`timeline-event-${ev.id || i}`}>
                        <span className={`absolute -left-[9px] top-0.5 w-4 h-4 rounded-full border-2 ${isEnter ? "bg-emerald-500 border-emerald-200" : "bg-slate-400 border-slate-200"}`} />
                        <div className="bg-white border border-slate-200 p-4">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <p className="font-display text-sm font-bold text-slate-900">
                                {fmtTime(ev.timestamp)}{" "}
                                <span className={`font-mono-tech text-[10px] font-bold uppercase tracking-[0.15em] ${isEnter ? "text-emerald-600" : "text-slate-500"}`}>
                                  {(ev.source || "").toUpperCase()} {isReentry ? "RE-ENTRY" : EVENT_LABEL[ev.event_type] || ev.event_type}
                                </span>
                              </p>
                              <p className="mt-1 text-xs text-slate-500">
                                {ev.branch_name || "No property"}
                                {ev.accuracy_m != null ? ` · GPS ±${Math.round(ev.accuracy_m)}m` : ""}
                                {ev.distance_m != null ? ` · ${Math.round(ev.distance_m)}m from centre` : ""}
                              </p>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {corrected ? (
                                  <span className="border border-blue-200 bg-blue-50 text-blue-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider" data-testid={`tl-corrected-${ev.id}`}>
                                    Corrected{ev.corrected_by ? ` by ${ev.corrected_by}` : ""}{ev.corrected_at ? ` · ${fmtDate(ev.corrected_at)}` : ""}
                                  </span>
                                ) : null}
                                {isGpsException(ev) && (
                                  <span className="border border-amber-300 bg-amber-50 text-amber-700 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider">GPS Exception</span>
                                )}
                              </div>
                              {(ev.correction_reason || ev.reason) && canCorrect && (
                                <p className="mt-1.5 text-[11px] text-slate-400 italic">Reason: {ev.correction_reason || ev.reason}</p>
                              )}
                            </div>
                            {canCorrect && (
                              <div className="flex items-center gap-2.5 shrink-0">
                                <button onClick={() => openCorrect(ev)} data-testid={`tl-correct-${ev.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline">
                                  <Pencil size={11} /> Correct
                                </button>
                                <button onClick={() => { setDeleting(ev); setForm({ ...form, reason: "" }); setFormError(""); }} data-testid={`tl-delete-${ev.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C5221F] hover:underline">
                                  <Trash2 size={11} /> Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </>
          )}
        </div>
      </div>

      {/* correct / add modal */}
      {correcting && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="tl-correct-modal" onClick={() => setCorrecting(null)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-slate-900">{correcting === "new" ? "Add Missed Attendance Event" : "Correct Attendance Event"}</h3>
            <p className="mt-1 text-xs text-slate-500">Recorded against the central attendance log with full audit history. Nothing is overwritten silently.</p>
            <form onSubmit={submitCorrection} className="mt-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Event</label>
                  <select data-testid="tl-form-type" className={inputCls} value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })}>
                    <option value="enter">Time-In</option>
                    <option value="exit">Time-Out</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date & Time *</label>
                  <input type="datetime-local" required data-testid="tl-form-time" className={inputCls} value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} />
                </div>
              </div>
              {correcting === "new" && (
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Property / Site *</label>
                  <select data-testid="tl-form-branch" className={inputCls} value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                    <option value="">Select property…</option>
                    {(branches || []).filter((b) => b.active !== false).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Correction Reason *</label>
                <textarea rows={2} required data-testid="tl-form-reason" className={inputCls} placeholder="Why is this correction needed?" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </div>
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="tl-form-error">{formError}</p>}
              <button type="submit" disabled={busy} data-testid="tl-form-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} {correcting === "new" ? "Add Event (Audited)" : "Save Correction (Audited)"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* delete modal */}
      {deleting && (
        <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" data-testid="tl-delete-modal" onClick={() => setDeleting(null)}>
          <div className="bg-white w-full max-w-md p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-lg font-bold text-slate-900">Delete Invalid Event</h3>
            <p className="mt-1 text-xs text-slate-500">{fmtTime(deleting.timestamp)} · {deleting.event_type === "enter" ? "Time-In" : "Time-Out"} · {deleting.branch_name || "No property"}. The deletion is kept in the audit log.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Reason *</label>
                <textarea rows={2} data-testid="tl-delete-reason" className={inputCls} placeholder="Why is this event invalid?" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
              </div>
              {formError && <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm">{formError}</p>}
              <button onClick={submitDelete} disabled={busy} data-testid="tl-delete-submit"
                className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} Delete Event (Audited)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Timeline;
